import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DevicesFacade} from '@kion/data-access/data-access-devices';
import {combineLatest, Observable} from 'rxjs';
import { Device, DeviceCart, DeviceFeature, Role } from '@kion/data-models';
import {map, take} from 'rxjs/operators';
import {FeatureEnablementsFacade} from '@kion/data-access/data-access-feature-enablements';
import {DeviceCartsFacade} from '@kion/data-access/data-access-device-carts';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {ToastService} from '@kion/ui';
import {TranslateService} from '@ngx-translate/core';
import {LmhSoftwareProductsFacade} from "@kion/data-access/data-access-lmh-software-products";
import { RolesFacade } from '@kion/data-access/data-access-roles';
import { SubSink } from 'subsink';
import { UsersFacade } from '@kion/data-access/data-access-users';

@Component({
  selector: 'kion-cms-device-detail',
  templateUrl: './device-detail.component.html',
  styleUrls: ['./device-detail.component.scss']
})
export class DeviceDetailComponent implements OnInit {

  factoryId: string;
  deviceId: string;

  private subSink: SubSink;

  device$: Observable<Device>;
  features$: Observable<DeviceFeature[]>;
  featuresEnablements$: Observable<DeviceFeature[]>;
  softwareProducts$: Observable<DeviceFeature[]>;
  deviceCart$: Observable<DeviceCart>;
  roles$: Observable<Role[]>;

  constructor(
    private route: ActivatedRoute,
    private devicesFacade: DevicesFacade,
    private featureEnablementsFacade: FeatureEnablementsFacade,
    private lmhSoftwareProductsFacade: LmhSoftwareProductsFacade,
    private deviceCartsFacade: DeviceCartsFacade,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private rolesFacade: RolesFacade,
    private usersFacade: UsersFacade
  ) {
    this.subSink = new SubSink();
  }

  ngOnInit(): void {
    this.factoryId = this.route.snapshot.params.factory;
    this.deviceId = this.route.snapshot.params.id;
    this.devicesFacade.loadById(this.factoryId, this.deviceId, this.factoryId + '_' + this.deviceId);
    this.featureEnablementsFacade.loadAll(this.deviceId, this.factoryId);
    this.lmhSoftwareProductsFacade.loadAll(this.deviceId);
    this.deviceCartsFacade.loadCartByDevice(this.factoryId, this.deviceId);
    this.subSink.sink = this.usersFacade.all$.subscribe(
      users => {
        const me = users.find(user => user.id === "1");
        this.rolesFacade.loadByUserId(me.uid, true);
      }
    );
    this.deviceCart$ = this.deviceCartsFacade.all$.pipe(
      map(
        (deviceCarts: DeviceCart[]) => deviceCarts.find(
          (deviceCart: DeviceCart) => deviceCart.deviceId === this.deviceId
        )
      )
    );
    this.device$ = this.devicesFacade.all$.pipe(
      map(
        (devices: Device[]) => devices.find(
          (device: Device) => device.id === this.deviceId && device.manufacturer.toLowerCase() === this.factoryId
        )
      )
    );
    this.roles$ = combineLatest(
      [
        this.device$,
        this.rolesFacade.all$
      ]
    ).pipe(
      map(
        ([device, roles]) => {
          if (device) {
            return roles.filter(
              (role: Role) => role.baseStore === device.networkPartner?.uid
            )
          }
        }
      )
    );
    this.featuresEnablements$ = combineLatest(
      [
        this.featureEnablementsFacade.all$,
        this.device$
      ]
    ).pipe(
      map(
        ([ features, device]) => features.filter(
          (feature: DeviceFeature) => device?.products?.map(f => f.code).includes(feature.id),
        )
      )
    );
    this.softwareProducts$ = combineLatest(
      [
        this.lmhSoftwareProductsFacade.all$,
        this.device$
      ]
    ).pipe(
      map(
        ([lhmSoftwareProducts, device]) => lhmSoftwareProducts.filter(
          (feature: DeviceFeature) => device?.products?.map(f => f.code).includes(feature.id),
        )
      )
    );
    this.features$= combineLatest(
      [
        this.softwareProducts$,
        this.featuresEnablements$
      ]
    ).pipe(
      map(
        ([softProducts,featProducts])=>featProducts.concat(softProducts)
      )
    )
  }

  //TODO: salesRule is optional
    addToCart(feature: { ean: string; salesRule: string }) {
    this.subSink.sink = this.deviceCart$.pipe(take(1)).subscribe(
      (cart: DeviceCart) => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.deviceCartsFacade.addToCart(this.factoryId, this.deviceId, cart.id, feature.ean, feature.salesRule),
          () => {
            this.toastService.show(
              {
                text: this.translateService.instant('feature.toast.addToBasket.success'),
                type: 'success'
              }
            );
          },
          () => {
            this.toastService.show(
              {
                text: this.translateService.instant('feature.toast.addToBasket.error'),
                type: 'error'
              }
            );
          }
        )
      }
    );
  }
}
